'use client';
import React from 'react';
import { InView } from 'react-intersection-observer';
import Hooks from '@benzinga/hooks';
import type { Mode } from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { loadTaboolaScript } from './utils';
import { useTaboolaAdImpression } from '../../hooks/useTaboolaAdImpression';

type Article = 'auto';
type TargetType = 'mix';

interface AddSettings {
  article?: Article;
  container?: string;
  mode?: Mode;
  placement?: string;
  target_type?: TargetType;
  flush?: boolean;
  url?: string;
  other?: string;
}

interface Props {
  unitKey?: string;
  url: string;
  id?: string;
  vizSensor?: boolean;
  mode?: Mode;
  placement?: string;
  container?: string;
  settings?: AddSettings;
  shouldFlush?: boolean;
}

export const TaboolaPlacement: React.FC<Props> = props => {
  const hasAdLight = useBenzingaEdge().adLightEnabled;
  if (hasAdLight) return null;

  return <TaboolaPlacementInner {...props} />;
};

const TaboolaPlacementInner: React.FC<Props> = ({
  container,
  id,
  mode = 'thumbnails-a',
  placement: initialPlacement,
  settings,
  shouldFlush = true,
  unitKey = 'benzinga-benzinga1',
  url,
  vizSensor = false,
}) => {
  const [loaded, setLoaded] = React.useState(false);
  const [pageCounter, setPageCounter] = React.useState<number>(1);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const unitSettings: AddSettings = React.useMemo(() => {
    return settings || { article: 'auto', url };
  }, [url, settings]);

  React.useEffect(() => {
    const counter = window['_taboola_page_counter']
      ? window['_taboola_page_counter']++
      : (window['_taboola_page_counter'] = 1);
    setPageCounter(counter);
    shouldFlush && addSettings({ flush: true });
    // Note: flush should be called once per page after all placements are added
    // Individual components should not call flush automatically
    // https://developers.taboola.com/web-integrations/docs/infinite-scroll#flush-the-queue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const containerId = container || `taboola-below-article-${id || pageCounter}`;
  const placement = initialPlacement || 'Infinite Scroll Page Feed';

  useTaboolaAdImpression({
    containerRef,
    placementName: placement,
  });

  const loadSettings = React.useCallback(() => {
    addSettings({
      container: containerId,
      mode: mode,
      placement,
      target_type: 'mix',
    });
  }, [containerId, mode, placement]);

  const load = React.useCallback(() => {
    loadTaboolaScript(unitKey, () => {
      loadSettings();
    });
    setLoaded(true);
  }, [unitKey, loadSettings]);

  Hooks.useEffectDidMount(() => {
    if (!loaded && !vizSensor) {
      if (!window['_taboola']) {
        window['_taboola'] = [];
      }

      addSettings(unitSettings);
      load();
    }
  });

  const addSettings = (settings: AddSettings): void => {
    if (Array.isArray(window['_taboola'])) {
      window['_taboola'].push(settings);
    }
  };

  const loadTaboola = (isVisible: boolean) => {
    if (!loaded && isVisible) {
      if (!window['_taboola']) {
        window['_taboola'] = [];
      }

      addSettings(unitSettings);
      load();
    }
  };

  const placeholder = React.useMemo(() => {
    return <div className="taboola-placeholder" id={containerId} ref={containerRef} style={{ minHeight: '400px' }} />;
  }, [containerId]);

  if (!vizSensor) {
    return placeholder;
  }

  return (
    <InView onChange={isVisible => loadTaboola(isVisible)} rootMargin="0px 0px -2000px 0px">
      {({ ref }) => <div ref={ref}>{placeholder}</div>}
    </InView>
  );
};

export default TaboolaPlacement;
